/**
 * WordPress dependencies
 */
import { useReducer, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import Button from '../Button/Button';
import TextInput from '../TextInput';
import handleSubmitForm from '../../utils/handleSubmitDataCaptureForm';
import validateEmail from '../../utils/validateEmail';

function DataCaptureForm({ formID }) {
	const formConfig = window.dekode_profundo_form[formID] ?? [];
	const formURL = formConfig['formURL'] ?? '';
	const formTitle = formConfig['formTitle'] ?? '';
	const formDescription = formConfig['formDescription'] ?? '';
	const formInstance = formConfig['formInstance'] ?? 0;
	const requiredFields = formConfig['requiredFields'] ??  [];
	const buttonText = formConfig['buttonText'] ??  __('Submit', 'dekode-profundo-forms');

	const [formData, setFormData] = useReducer(
		( s, a ) => ({ ...s, ...a }),
		{
			loading: false,
			name: '',
			firstname: '',
			lastname: '',
			mobile: '',
			email: '',
			errors: {},
			message: '',
		}
	);

	const {
		loading,
		name,
		mobile,
		email,
		errors,
	} = formData;

	const nameRef = useRef('');

	const validateForm = () => {
		let fields = {};

		const addError = (field, message) => {
			if ( undefined === fields[field] ) {
				fields[field] = [];
			}

			fields[field].push(message);
		}

		// Default to require mobile and name if no fields are set to required and it's not an anonymous form.
		const defaultRequiredFields = [ 'mobile', 'name' ];
		const required = requiredFields.length ? requiredFields : defaultRequiredFields;

		// Add error if a required field is missing.
		required.forEach((field) => {
			if ( 0 === formData[field].length ) {
				addError(field, __('This field is required', 'dekode-profundo-forms'));
			}
		});

		// Check for valid email address.
		if ( email.length > 0 ) {
			if ( !validateEmail(email) ) {
				addError('email', __('Please enter a valid email address', 'dekode-profundo-forms'));
			}
		}

		if (0 === Object.keys(fields).length) {
			setFormData({ errors: [] });
			handleSubmitForm(formURL, formData, setFormData);
		} else {
			setFormData({ errors: fields });
		}
	}

	const generateName = (name) => {
		return name + formInstance;
	}

	const handleMobileChange = (mobile) => {
		setFormData({ mobile });

		if ( mobile.length >= 8 ) {
			fetch('https://gi.blakors.no/api/mobil?' + mobile)
			.then((response) => {
				return response.json();
			})
			.then((response) => {
				let newData = [];
				if ( response.navn && 0 === nameRef.current.length ) {
					newData['name'] = response.navn;
				}

				setFormData({ ...newData });
			});
		}
	}

	const handleNameChange = (name) => {
		setFormData({ name });
		nameRef.current = name;
	}

	return (
		<form className="dpf_data-capture-form" noValidate>
			<div className="dpf_step" id="step1">
				<h2 class="dpf_form-title">{formTitle}</h2>
				<p class="dpf_form-description">{formDescription}</p>

				<div className="dpf_fields">
					<TextInput
						name={generateName('mobile')}
						label={__('Mobile', 'dekode-profundo-forms')}
						onChange={handleMobileChange}
						value={mobile}
						required={0 === requiredFields.length || requiredFields.includes('mobile')}
						error={errors['mobile'] ?? null}
						disabled={loading}
					/>
					<TextInput
						name={generateName('fullname')}
						label={__('Full Name', 'dekode-profundo-forms')}
						onChange={handleNameChange}
						value={name}
						required={0 === requiredFields.length || requiredFields.includes('name')}
						error={errors['name'] ?? null}
						disabled={loading}
					/>
					<TextInput
						name={generateName('email')}
						label={__('Email', 'dekode-profundo-forms')}
						onChange={(email) => setFormData({ email })}
						value={email}
						required={requiredFields.includes('email')}
						error={errors['email'] ?? null}
						disabled={loading}
					/>
				</div>

				<div className="dpf_actions">
					<Button
						onClick={() => validateForm()}
						type="submit"
						customClass={ loading ? 'dpf_button--loading' : null}
						disabled={loading}
					>
						{buttonText}
					</Button>
				</div>
			</div>
		</form>
	);
}

export default DataCaptureForm;
